import { FC, useEffect, useRef, useState } from 'react';

import { PhotoVideo } from './AuthLayout.styled';

interface AuthHeroVideoProps {
  poster: string;
  webm: string;
  mp4: string;
}

type Connection = { saveData?: boolean; effectiveType?: string };

const START_FALLBACK_MS = 2500;
const READY_TIMEOUT_MS = 8000;

/* Czy w ogóle pobierać wideo: nie przy reduced-motion, Save-Data, wolnej sieci (2g/3g) ani gdy panel jest ukryty (telefon). */
const shouldLoadVideo = (element: HTMLElement): boolean => {
  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  } catch { /* ignore */ }
  const { connection } = (navigator as Navigator & { connection?: Connection });
  if (connection?.saveData === true) return false;
  if (/(^|-)2g$|^3g$/.test(connection?.effectiveType ?? '')) return false;
  return element.offsetParent !== null;
};

/* Wideo w tle jak w hero landingu i audytu: wyciszone, bez kontrolek, w pętli (klip tam i z powrotem).
   Start: tylko poster (preload="none", źródła dodawane później), więc nic nie konkuruje z formularzem.
   Po window.load (albo po 2,5 s) dokładamy źródła i odtwarzamy; jeśli w 8 s nie gra, zostaje poster. */
export const AuthHeroVideo: FC<AuthHeroVideoProps> = ({ poster, webm, mp4 }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoadVideo(video)) return undefined;

    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      setIsActive(true);
    };

    if (document.readyState === 'complete') {
      const timer = window.setTimeout(run, 0);
      return () => window.clearTimeout(timer);
    }
    window.addEventListener('load', run, { once: true });
    const fallback = window.setTimeout(run, START_FALLBACK_MS);
    return () => {
      window.removeEventListener('load', run);
      window.clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!isActive || !video) return undefined;

    const giveUp = () => setIsActive(false);
    const timer = window.setTimeout(() => {
      if (video.readyState < 3) giveUp();
    }, READY_TIMEOUT_MS);
    const onCanPlay = () => window.clearTimeout(timer);

    video.addEventListener('canplay', onCanPlay, { once: true });
    video.addEventListener('error', giveUp, { once: true });
    video.load();
    video.play().catch(() => { /* autoplay zablokowany: zostaje poster */ });

    return () => {
      window.clearTimeout(timer);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('error', giveUp);
    };
  }, [isActive]);

  return (
    <PhotoVideo
      ref={videoRef}
      poster={poster}
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
    >
      {isActive && <source src={webm} type="video/webm" />}
      {isActive && <source src={mp4} type="video/mp4" />}
    </PhotoVideo>
  );
};
